import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Button, Select, Table, Tag, message } from 'antd'
import styled from 'styled-components'
import useIsBlocked from 'src/hooks/useIsBlocked'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import {
  listVenueProductIssues,
  updateVenueProductIssueStatus,
} from 'src/utils/productIssueApi'
import {
  PRODUCT_ISSUE_STATUS,
  getProductIssueCategoryLabel,
  getProductIssueStatusLabel,
} from 'src/utils/productIssue'

const { Option } = Select

const statusColor = {
  reported: 'orange',
  in_progress: 'blue',
  resolved: 'green',
}

const ProductIssues = ({ venueId }) => {
  const blocked = useIsBlocked('product-issues')
  const location = useLocation()
  const history = useHistory()

  const view = useMemo(() => {
    const params = new URLSearchParams(location.search || '')
    return params.get('view') === 'resolved' ? 'resolved' : 'open'
  }, [location.search])

  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [updatingId, setUpdatingId] = useState(null)

  const load = useCallback(async () => {
    if (!venueId) return
    setLoading(true)
    try {
      const data = await listVenueProductIssues(venueId, { view })
      setItems(data?.items || [])
    } catch (error) {
      message.error(error.message || 'Could not load product issues')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [venueId, view])

  useEffect(() => {
    void load()
  }, [load])

  if (blocked) {
    return <PermissionBlockScreen />
  }

  const setView = next => {
    const base = `/v/${venueId}/purchasing/product-issues`
    history.push(next === 'resolved' ? `${base}?view=resolved` : base)
  }

  const handleStatusChange = async (issueId, status) => {
    setUpdatingId(issueId)
    try {
      await updateVenueProductIssueStatus(venueId, issueId, { status })
      message.success('Status updated')
      await load()
    } catch (error) {
      message.error(error.message || 'Could not update status')
    } finally {
      setUpdatingId(null)
    }
  }

  const columns = [
    {
      title: 'Reported',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: value => (value ? new Date(value).toLocaleString('en-GB') : '—'),
      width: 160,
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierTitle',
      key: 'supplierTitle',
      ellipsis: true,
    },
    {
      title: 'Product',
      key: 'product',
      render: (_, row) => (
        <div>
          <div>{row.productSnapshot?.description || '—'}</div>
          <Muted>{row.productSnapshot?.productCode || ''}</Muted>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: value => getProductIssueCategoryLabel(value),
    },
    {
      title: 'Explanation',
      dataIndex: 'explanation',
      key: 'explanation',
      ellipsis: true,
      render: value => value || '—',
    },
    {
      title: 'Routed',
      key: 'routing',
      width: 110,
      render: (_, row) => {
        if (row.routing?.sent) return <Tag color='green'>Emailed</Tag>
        if (row.routing?.attempted)
          return (
            <Tag color='default' title={row.routing?.error || ''}>
              No email
            </Tag>
          )
        return <Tag>—</Tag>
      },
    },
    {
      title: 'Reported by',
      dataIndex: 'reportedByName',
      key: 'reportedByName',
      render: value => value || '—',
    },
    {
      title: 'Status',
      key: 'status',
      width: 180,
      render: (_, row) => (
        <Select
          size='small'
          value={row.status}
          style={{ width: 150 }}
          loading={updatingId === row._id}
          onChange={status => handleStatusChange(row._id, status)}
        >
          <Option value={PRODUCT_ISSUE_STATUS.REPORTED}>
            {getProductIssueStatusLabel(PRODUCT_ISSUE_STATUS.REPORTED)}
          </Option>
          <Option value={PRODUCT_ISSUE_STATUS.IN_PROGRESS}>
            {getProductIssueStatusLabel(PRODUCT_ISSUE_STATUS.IN_PROGRESS)}
          </Option>
          <Option value={PRODUCT_ISSUE_STATUS.RESOLVED}>
            {getProductIssueStatusLabel(PRODUCT_ISSUE_STATUS.RESOLVED)}
          </Option>
        </Select>
      ),
    },
    {
      title: '',
      key: 'badge',
      width: 100,
      render: (_, row) => (
        <Tag color={statusColor[row.status] || 'default'}>
          {getProductIssueStatusLabel(row.status)}
        </Tag>
      ),
    },
  ]

  return (
    <Wrapper>
      <HeaderRow>
        <div>
          <h2 style={{ margin: 0 }}>Product Issues</h2>
          <Muted>
            Catalogue data errors reported from products, orders, and invoices.
          </Muted>
        </div>
        <ButtonGroup>
          <Button
            type={view === 'open' ? 'primary' : 'default'}
            onClick={() => setView('open')}
          >
            Open
          </Button>
          <Button
            type={view === 'resolved' ? 'primary' : 'default'}
            onClick={() => setView('resolved')}
          >
            Resolved
          </Button>
        </ButtonGroup>
      </HeaderRow>

      <Table
        rowKey={row => row._id}
        loading={loading}
        columns={columns}
        dataSource={items}
        pagination={{ pageSize: 25 }}
        scroll={{ x: 1100 }}
        locale={{
          emptyText:
            view === 'resolved'
              ? 'No resolved product issues yet.'
              : 'No open product issues.',
        }}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding: 24px;
  background: #fff;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`

const Muted = styled.div`
  color: #8c8c8c;
  font-size: 12px;
  margin-top: 4px;
`

export default ProductIssues
